/**
 * SuperAdmin → Planes: el catálogo de planes de Finance 360 (precio, límites y módulos).
 * Cada cuenta guarda el código de su plan; al asignarlo en la cuenta se toman estos límites.
 * Los módulos se muestran y se venden con el plan; todavía no esconden pantallas.
 */
import React, { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { Tags, Save, Star } from 'lucide-react';
import SuperadminLayout from '../components/SuperadminLayout';
import { panelApi } from '../utils/api';
import { Encabezado, Panel, Boton, Cargando, Aviso, Campo, inputCls } from '../components/ui';

const usd = (n) => `US$${(Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

function TarjetaPlan({ plan, modulos, onGuardado, onError }) {
  const [f, setF] = useState(plan);
  const [guardando, setGuardando] = useState(false);
  useEffect(() => setF(plan), [plan]);
  const cambiado = JSON.stringify(f) !== JSON.stringify(plan);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const alternar = (m) => setF({ ...f, modulos: f.modulos.includes(m) ? f.modulos.filter(x => x !== m) : [...f.modulos, m] });

  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await panelApi.guardarPlan(plan.codigo, f);
      onGuardado(r.data.plan);
    } catch (e) {
      onError(e.response?.data?.error || 'No se pudo guardar el plan');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Panel titulo={<span className="flex items-center gap-2">{f.nombre}{f.destacado && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400" />}<span className="text-xs font-normal text-gray-500">{plan.codigo}</span></span>}
      icono={Tags}
      accion={<span className={clsx('text-xs font-semibold px-2 py-0.5 rounded-full', f.activo ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500')}>{f.activo ? 'Activo' : 'Inactivo'}</span>}>
      <div className="grid grid-cols-2 gap-3">
        <Campo label="Nombre" className="col-span-2"><input className={inputCls} value={f.nombre} onChange={set('nombre')} /></Campo>
        <Campo label="Descripción" className="col-span-2"><input className={inputCls} value={f.descripcion || ''} onChange={set('descripcion')} /></Campo>
        <Campo label="Precio mensual (US$, sin IVA)"><input type="number" min="0" step="0.01" className={inputCls} value={f.precioMensual} onChange={set('precioMensual')} /></Campo>
        <Campo label="Precio anual (US$)" ayuda={`Equivale a ${usd(f.precioAnual / 12)} al mes`}><input type="number" min="0" step="0.01" className={inputCls} value={f.precioAnual} onChange={set('precioAnual')} /></Campo>
        <Campo label="Empresas incluidas"><input type="number" min="1" className={inputCls} value={f.maxEmpresas} onChange={set('maxEmpresas')} /></Campo>
        <Campo label="Usuarios incluidos"><input type="number" min="1" className={inputCls} value={f.maxUsuarios} onChange={set('maxUsuarios')} /></Campo>
        <Campo label="Empresa adicional (US$/mes)" ayuda="Vacío = no se venden empresas adicionales">
          <input type="number" min="0" step="0.01" className={inputCls} value={f.empresaAdicional ?? ''} onChange={set('empresaAdicional')} />
        </Campo>
        <div className="flex flex-col justify-end gap-2 text-sm text-gray-700">
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.destacado} onChange={set('destacado')} /> Destacado en el sitio</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.activo} onChange={set('activo')} /> Se puede asignar</label>
        </div>
      </div>
      <p className="text-xs font-medium text-gray-700 mt-5 mb-2">Módulos incluidos</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {modulos.map(([id, nombre]) => (
          <label key={id} className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={f.modulos.includes(id)} onChange={() => alternar(id)} />{nombre}
          </label>
        ))}
      </div>
      <div className="flex justify-end mt-5">
        <Boton variante="primario" icono={Save} cargando={guardando} disabled={!cambiado} onClick={guardar}>Guardar plan</Boton>
      </div>
    </Panel>
  );
}

export default function Planes() {
  const [planes, setPlanes] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [aviso, setAviso] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const r = await panelApi.planes();
      setPlanes(r.data.planes || []);
      setModulos(r.data.modulos || []);
    } catch (e) {
      setAviso({ tipo: 'error', texto: e.response?.data?.error || 'No se pudieron leer los planes' });
    } finally {
      setCargando(false);
    }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  return (
    <SuperadminLayout>
      <Encabezado titulo="Planes" subtitulo="Precio, límites y módulos de cada plan. Al asignar un plan a una cuenta se toman sus límites; el sitio web publica estos precios." />
      <Aviso aviso={aviso} onClose={() => setAviso(null)} />
      {cargando ? <Cargando texto="Leyendo los planes…" /> : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {planes.map(p => (
            <TarjetaPlan key={p.codigo} plan={p} modulos={modulos}
              onGuardado={(nuevo) => { setPlanes(planes.map(x => x.codigo === nuevo.codigo ? nuevo : x)); setAviso({ tipo: 'ok', texto: `Plan ${nuevo.nombre} guardado` }); }}
              onError={(texto) => setAviso({ tipo: 'error', texto })} />
          ))}
        </div>
      )}
    </SuperadminLayout>
  );
}
